import React, { useState, useEffect } from 'react';
import FilterBar from './FilterBar';
import NoQuestionsMessage from './NoQuestionsMessage';
import QuestionCard from './QuestionCard';

import { QuestionCounter } from '../utils/questionCounters';
import { AppBook } from '../types/utils';
// CSS removed - using utility classes instead

export interface QuestionListProps {
  book: AppBook;
  defaultQuestionType: 'true_false' | 'multiple_choice';
  selectedBook: string;
  selectedChapter: string;
  availableBooks: string[];
  availableChapters: string[];
  onBookChange: (bookId: string) => void;
  onChapterChange: (chapterId: string) => void;
}

const QuestionList: React.FC<QuestionListProps> = ({
  book,
  defaultQuestionType,
  selectedBook,
  selectedChapter,
  availableBooks,
  availableChapters,
  onBookChange,
  onChapterChange
}) => {
  const [selectedQuestionType, setSelectedQuestionType] = useState<'true_false' | 'multiple_choice'>(defaultQuestionType);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());

  // Update selected question type when defaultQuestionType changes
  useEffect(() => {
    setSelectedQuestionType(defaultQuestionType);
  }, [defaultQuestionType]);

  const handleQuestionTypeChange = (questionType: 'true_false' | 'multiple_choice') => {
    setSelectedQuestionType(questionType);
  };

  const toggleQuestion = (questionId: string) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(questionId)) {
      newExpanded.delete(questionId);
    } else {
      newExpanded.add(questionId);
    }
    setExpandedQuestions(newExpanded);
  };

  // Get questions from the book structure
  const getCurrentQuestions = () => {
    const allQuestions: Array<{id: string, question: any, chapterId: string}> = [];
    
    Object.entries(book.chapters).forEach(([chapterId, chapter]) => {
      // Skip if filtering by specific chapter
      if (selectedChapter !== 'all' && chapterId !== selectedChapter) {
        return;
      }
      
      const questions = selectedQuestionType === 'true_false' 
        ? chapter.trueFalseQuestions 
        : chapter.multipleChoiceQuestions;
        
      questions.forEach(q => {
        allQuestions.push({
          id: q.id,
          question: q,
          chapterId: chapterId
        });
      });
    });
    
    return allQuestions;
  };

  const currentQuestions = getCurrentQuestions();

  return (
    <div className="question-list">
      <FilterBar
        selectedBook={selectedBook}
        selectedChapter={selectedChapter}
        selectedQuestionType={selectedQuestionType}
        availableBooks={availableBooks}
        availableChapters={availableChapters}
        trueFalseCount={QuestionCounter.getTotalBookCounts(book).trueFalse}
        multipleChoiceCount={QuestionCounter.getTotalBookCounts(book).multipleChoice}
        onBookChange={onBookChange}
        onChapterChange={onChapterChange}
        onQuestionTypeChange={handleQuestionTypeChange}
      />

      <div className="questions-container">
        {currentQuestions.length === 0 ? (
          <NoQuestionsMessage
            selectedBook={selectedBook}
            selectedChapter={selectedChapter}
            selectedQuestionType={selectedQuestionType}
          />
        ) : (
          currentQuestions.map((questionItem, index) => (
            <QuestionCard
              key={questionItem.id}
              pageId={questionItem.id}
              chapterId={questionItem.chapterId}
              index={index}
              content={`Chapter ${questionItem.chapterId}`}
              questions={[questionItem.question]}
              isExpanded={expandedQuestions.has(questionItem.id)}
              onToggle={toggleQuestion}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default QuestionList; 